/**
 * Turns what `FlowRunner` sees into the published `trustsy:*` messages, for any
 * host that listens: the iframe embed posts them to `window.parent`, the web
 * SDK (zoid) hands them to the partner page's `onFlowEvent` prop.
 *
 * `trustsy:ready` once the flow has loaded, `trustsy:state` once per step,
 * `trustsy:done` (or `trustsy:error flow_failed`) once, `trustsy:error` when
 * the flow cannot load. The messages carry no applicant data.
 */
import { useCallback, useRef } from 'react';

import type { CollectionFlowSnapshot } from '../flow/types';
import { doneMessage } from './flowMessages';
import type { EmbedMessage } from './index';

export type FlowMessageSink = (message: EmbedMessage) => void;

export function useFlowReporter(send: FlowMessageSink | null) {
  const ready = useRef(false);
  const lastState = useRef<string | null>(null);
  const finished = useRef(false);

  const onSnapshot = useCallback(
    (snapshot: CollectionFlowSnapshot) => {
      if (!send) return;
      if (!ready.current) {
        ready.current = true;
        send({ type: 'trustsy:ready' });
      }
      if (snapshot.state && snapshot.state !== lastState.current) {
        lastState.current = snapshot.state;
        send({ type: 'trustsy:state', state: snapshot.state });
      }
      const done = finished.current ? null : doneMessage(snapshot);
      if (done) {
        finished.current = true;
        send(done);
      }
    },
    [send],
  );

  const onLoadError = useCallback(
    (refused: boolean) => {
      send?.({ type: 'trustsy:error', code: refused ? 'access_refused' : 'load_failed' });
    },
    [send],
  );

  return { onSnapshot, onLoadError };
}
